<template>
    <v-card v-if="modelValue" rounded="0" class="trip-pattern-details search-result search-result--mobile" :class="cssClasses">
        <v-card-text class="pa-0" :class="{ 'overflow-y-auto': !showMapView }">
            <div v-if="!showMapView" class="search-result__header flex-column">
                <div class="d-flex flex-nowrap pa-4 pt-3">
                    <div class="trip-leg-summary__container">
                        <trip-leg-summary
                            :model-value="modelValue.legs"
                            active-summary
                            variant="expanded"
                            no-wrap
                            class="flex-1-1 text-truncate"
                        />
                    </div>
                    <v-spacer />
                    <div class="py-2">
                        <total-duration v-if="modelValue.duration" :model-value="modelValue.duration" />
                    </div>
                </div>
                <v-divider opacity="1" color="primary" thickness="1" />
            </div>
            <div class="search-result__content">
                <div v-if="showMapView" class="pa-4 pt-2">
                    <trip-pattern-summary :model-value="modelValue" :loading="loading" />
                    <div v-if="notificationMessages.length" class="mt-3">
                        <notification-messages-list
                            :messages="notificationMessages"
                            :loading="loading"
                            :compact="showMapView"
                            :disabled="!showMapView"
                        />
                    </div>
                </div>

                <template v-else-if="hasDetails">
                    <slot name="searchResultContentPrepend" />

                    <trip-leg-list v-if="modelValue?.legs" class="pa-4">
                        <trip-leg-list-item
                            v-for="(leg, i) in modelValue.legs"
                            :key="leg.id"
                            ref="tripLegItems"
                            :model-value="leg"
                            :first="i === 0"
                            :last="i === modelValue.legs.length - 1"
                            :previous-leg="modelValue.legs[i - 1]"
                            :next-leg="modelValue.legs[i + 1]"
                            class="text-truncate"
                        />
                    </trip-leg-list>

                    <slot name="searchResultContentAppend" />
                </template>
            </div>
        </v-card-text>
        <div class="search-result__footer" :class="{ 'elevation-2': !showMapView }">
            <v-divider v-if="showMapView" class="mx-4" />
            <div class="pa-4">
                <v-defaults-provider :defaults="actionBtnOptions">
                    <v-row v-if="showMapView">
                        <v-col cols="6">
                            <v-btn
                                variant="outlined"
                                height="48px"
                                :text="t('common.resources.back', 'Back')"
                                @click="onHideMapViewBtnClick"
                            />
                        </v-col>
                    </v-row>
                    <v-row v-else class="flex-nowrap">
                        <v-col cols="5" class="flex-grow-1" style="max-width: 50%">
                            <v-btn variant="outlined" height="48px" :text="t('common.resources.back', 'Back')" @click="onBackBtnClick" />
                        </v-col>
                        <v-col cols="auto" class="pa-0 d-flex align-center justify-center">
                            <slot name="between-action-buttons" />
                        </v-col>
                        <v-col v-if="hasDetails" cols="5" class="flex-grow-1" style="max-width: 50%">
                            <v-btn
                                variant="flat"
                                height="48px"
                                :text="t('common.resources.seeMapView', 'See map view')"
                                @click="onShowMapViewBtnClick"
                            />
                        </v-col>
                    </v-row>
                </v-defaults-provider>
            </div>
        </div>
    </v-card>
</template>

<script setup lang="ts">
import { NotificationMessageType, PlaceDto, TripPatternDto } from '@/types/webapi';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { TripPatternSummary, TripLegList, TripLegListItem } from '..';
import { NotificationMessagesList } from '@/features/common/components';
import { isTransitMode } from '@/features/trips/helpers';
import { type VCard } from 'vuetify/components';
import { TripLegSummary } from '..';
import { TotalDuration } from '../list';
import { uniqBy } from 'lodash-es';

interface Props extends /* @vue-ignore */ NonNullable<InstanceType<typeof VCard>['$props']> {
    /** The trip pattern to display. */
    modelValue?: TripPatternDto;

    /** The destination of the trip. */
    destination?: PlaceDto;

    /** Whether to show the loading indicator */
    loading?: NonNullable<InstanceType<typeof VCard>['$props']['loading']>;
}

interface Emits {
    (e: 'click:back'): void;
}

const { t } = useI18n();
const props = defineProps<Props>();
const emits = defineEmits<Emits>();
const showMapView = defineModel<boolean>('expanded');
const cssClasses = computed(() => [{ 'search-result--expanded': !showMapView.value }]);
const hasDetails = computed(
    () => props.modelValue && !(props.modelValue.legs?.length === 1 && !isTransitMode(props.modelValue.legs[0].tripMode))
);
const actionBtnOptions = {
    VBtn: {
        block: true,
        color: 'primary',
        size: 'large',
        rounded: 'lg',
        class: 'font-weight-bold'
    }
};
const notificationMessages = computed(() =>
    uniqBy(
        props.modelValue?.legs
            ?.flatMap(l => l.notificationMessages || [])
            .filter(
                x => x.type !== NotificationMessageType.BookingConfirmation && x.type !== NotificationMessageType.OnlineBookingArrangement
            ),
        'id'
    )
);

function onBackBtnClick() {
    emits('click:back');
}

function onShowMapViewBtnClick() {
    showMapView.value = true;
}

function onHideMapViewBtnClick() {
    showMapView.value = false;
}
</script>

<style lang="scss">
.trip-pattern-details.search-result--mobile {
    --v-color-trip-leg-bg: var(--search-result-bg-color);

    .trip-leg-summary {
        --trip-leg-summary-line-color: var(--v-color-neutral-600);
    }

    &.search-result--expanded {
        height: 100% !important;

        .search-result__header {
            position: sticky;
            top: 0;
            z-index: 10;
            background-color: #e2ece6;
        }
    }
}
</style>
